import { Plus, Trash2 } from 'lucide-react';
import { BlocoCampos, CampoArea, CampoTexto, Grade, Sugestoes } from '../componentes/campos';
import { FiguraMarcada, proximoNumero } from '../componentes/figura-marcada';
import { ListaEditavel } from '../componentes/lista-editavel';
import { SlotImagem } from '../componentes/slot-imagem';
import { CATALOGO_VISTAS } from '../manual-catalogos';
import { novaChamada, novaEspecificacao, novaVista } from '../manual-defaults';
import type { ItemEspecificacao, ItemVista } from '../manual-types';
import type { CampoLista } from '../use-manual-editor';
import type { PropsSecao } from './tipos';

const COLUNAS_ESPEC = [
  { chave: 'item' as const, rotulo: 'Item', placeholder: 'Ex.: Força nominal', largura: '1.3fr' },
  { chave: 'valor' as const, rotulo: 'Especificação', placeholder: 'Ex.: 30' },
  { chave: 'unidade' as const, rotulo: 'Unidade', placeholder: 'tf', largura: '0.5fr' },
];

export function SecaoVisaoGeral({ editor }: PropsSecao) {
  const { dados, definirCampo, adicionarItem, atualizarItem, removerItem, moverItem } = editor;

  const tabelaEspec = (campo: CampoLista, itens: ItemEspecificacao[], rotulo: string) => (
    <ListaEditavel<ItemEspecificacao>
      itens={itens}
      colunas={COLUNAS_ESPEC}
      rotuloAdicionar={`Adicionar linha (${rotulo})`}
      vazio="Nenhuma linha adicional cadastrada."
      aoAdicionar={() => adicionarItem(campo, novaEspecificacao())}
      aoAtualizar={(id, patch) => atualizarItem(campo, id, patch)}
      aoRemover={(id) => removerItem(campo, id)}
      aoMover={(id, direcao) => moverItem(campo, id, direcao)}
    />
  );

  return (
    <>
      <BlocoCampos titulo="Descrição da máquina" descricao="Capítulo 3 — texto de apresentação e princípio de funcionamento.">
        <CampoArea
          rotulo="Descrição funcional"
          obrigatorio
          linhas={5}
          valor={dados.descricao}
          placeholder="Função, processo executado, conjuntos principais e sistema de comando."
          aoMudar={(valor) => definirCampo('descricao', valor)}
        />
        <CampoArea
          rotulo="Princípio de funcionamento"
          linhas={4}
          valor={dados.principioFuncionamento}
          placeholder="Como a máquina executa o ciclo: acionamento, transmissão, controle e fim de curso."
          aoMudar={(valor) => definirCampo('principioFuncionamento', valor)}
        />
        <Grade>
          <CampoTexto rotulo="Aplicação / processo" obrigatorio valor={dados.aplicacao} placeholder="Ex.: Prensagem de buchas" aoMudar={(valor) => definirCampo('aplicacao', valor)} />
          <CampoTexto rotulo="Capacidade" valor={dados.capacidade} placeholder="Ex.: 30 tf" aoMudar={(valor) => definirCampo('capacidade', valor)} />
          <CampoTexto rotulo="Dimensões (L × A × P)" valor={dados.dimensoes} placeholder="Ex.: 1200 × 2300 × 900" aoMudar={(valor) => definirCampo('dimensoes', valor)} />
          <CampoTexto rotulo="Peso (kg)" valor={dados.peso} placeholder="Ex.: 1850" aoMudar={(valor) => definirCampo('peso', valor)} />
          <CampoTexto rotulo="Tensão de alimentação (V)" valor={dados.tensao} placeholder="Ex.: 440" aoMudar={(valor) => definirCampo('tensao', valor)} />
        </Grade>
      </BlocoCampos>

      <BlocoCampos
        titulo="Vistas da máquina"
        descricao="Cada vista gera uma figura no documento, com legenda e tabela de chamadas numeradas."
        acoes={<Sugestoes titulo="Adicionar" itens={CATALOGO_VISTAS} aoEscolher={(titulo) => adicionarItem('vistas', novaVista(titulo))} />}
      >
        <ListaEditavel<ItemVista>
          itens={dados.vistas}
          colunas={[
            { chave: 'titulo', rotulo: 'Título da vista', placeholder: 'Ex.: Vista frontal' },
            { chave: 'legenda', rotulo: 'Legenda', placeholder: 'Ex.: Figura 2 — vista frontal da prensa', largura: '1.6fr' },
          ]}
          rotuloAdicionar="Adicionar vista"
          vazio="Nenhuma vista cadastrada. As figuras começam vazias — envie as imagens da máquina levantada."
          aoAdicionar={() => adicionarItem('vistas', novaVista())}
          aoAtualizar={(id, patch) => atualizarItem('vistas', id, patch)}
          aoRemover={(id) => removerItem('vistas', id)}
          aoMover={(id, direcao) => moverItem('vistas', id, direcao)}
          detalhe={(vista) => (
            <div className="manual-vista-detalhe">
              <SlotImagem
                rotulo="Imagem da vista"
                descricao="Clique ou arraste a foto correspondente a esta vista."
                imagem={vista.imagem}
                altura={260}
                aoDefinir={(imagem) => atualizarItem('vistas', vista.id, { imagem })}
                conteudo={vista.imagem ? (
                  <FiguraMarcada
                    imagem={vista.imagem}
                    alt={vista.titulo || 'Vista'}
                    alturaMaxima={242}
                    marcadores={vista.chamadas
                      .filter((chamada) => typeof chamada.x === 'number' && typeof chamada.y === 'number')
                      .map((chamada) => ({ id: chamada.id, numero: chamada.numero, x: chamada.x as number, y: chamada.y as number, lx: chamada.lx, ly: chamada.ly }))}
                    aoCriar={(posicao) => atualizarItem('vistas', vista.id, {
                      chamadas: [...vista.chamadas, novaChamada(proximoNumero(vista.chamadas.map((chamada) => chamada.numero)), posicao)],
                    })}
                    aoMover={(id, patch) => atualizarItem('vistas', vista.id, {
                      chamadas: vista.chamadas.map((atual) => atual.id === id ? { ...atual, ...patch } : atual),
                    })}
                  />
                ) : undefined}
              />
              <div className="manual-chamadas">
                <div className="manual-chamadas-topo">
                  <strong>Chamadas numéricas</strong>
                  <button
                    type="button"
                    className="manual-ghost-button"
                    onClick={() => atualizarItem('vistas', vista.id, {
                      chamadas: [...vista.chamadas, novaChamada(String(vista.chamadas.length + 1))],
                    })}
                  >
                    <Plus />Adicionar chamada
                  </button>
                </div>
                {vista.imagem && (
                  <p className="manual-marcador-dica">
                    Pressione sobre o componente na foto e <strong>arraste para o lado</strong> para criar a seta numerada; um clique sem arrastar cria só o número.
                    Depois arraste o número ou a ponta para ajustar. Remover a chamada remove a seta.
                  </p>
                )}
                {vista.chamadas.length === 0 && <p className="manual-lista-vazia">Sem chamadas nesta vista.</p>}
                {vista.chamadas.map((chamada) => (
                  <div key={chamada.id} className="manual-chamada-linha">
                    <input
                      value={chamada.numero}
                      aria-label="Número da chamada"
                      placeholder="1"
                      onChange={(evento) => atualizarItem('vistas', vista.id, {
                        chamadas: vista.chamadas.map((atual) => atual.id === chamada.id ? { ...atual, numero: evento.target.value } : atual),
                      })}
                    />
                    <input
                      value={chamada.descricao}
                      aria-label="Descrição da chamada"
                      placeholder="Ex.: Botão de emergência"
                      onChange={(evento) => atualizarItem('vistas', vista.id, {
                        chamadas: vista.chamadas.map((atual) => atual.id === chamada.id ? { ...atual, descricao: evento.target.value } : atual),
                      })}
                    />
                    <button
                      type="button"
                      className="is-danger"
                      title="Remover chamada"
                      onClick={() => atualizarItem('vistas', vista.id, {
                        chamadas: vista.chamadas.filter((atual) => atual.id !== chamada.id),
                      })}
                    >
                      <Trash2 />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        />
      </BlocoCampos>

      <BlocoCampos titulo="Especificações técnicas" descricao="As linhas fixas (modelo, dimensões, peso, tensão) vêm dos campos acima; aqui entram as linhas complementares.">
        <div className="manual-subtitulo">Dados gerais</div>
        {tabelaEspec('especGerais', dados.especGerais, 'geral')}
        {dados.opcoes.incluirEspecEletricas && (
          <>
            <div className="manual-subtitulo">Dados elétricos</div>
            {tabelaEspec('especEletricas', dados.especEletricas, 'elétrico')}
          </>
        )}
        {dados.opcoes.incluirEspecHidraulicas && (
          <>
            <div className="manual-subtitulo">Dados hidráulicos</div>
            {tabelaEspec('especHidraulicas', dados.especHidraulicas, 'hidráulico')}
          </>
        )}
        {dados.opcoes.incluirEspecPneumaticas && (
          <>
            <div className="manual-subtitulo">Dados pneumáticos</div>
            {tabelaEspec('especPneumaticas', dados.especPneumaticas, 'pneumático')}
          </>
        )}
        <p className="manual-dica">
          As tabelas hidráulica e pneumática são ativadas na etapa <strong>Documento</strong>, junto das demais opções de composição.
        </p>
      </BlocoCampos>
    </>
  );
}
